import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ShieldCheck, AlertTriangle, PencilLine, Sparkles, Eye } from "lucide-react";

const GREETING_MODES = [
  { v: "title_surname", label: "Title + surname — Dear Mr. Adebayo," },
  { v: "title_full", label: "Title + full name — Dear Dr. Remi Adebayo," },
  { v: "title_given", label: "Title + first name — Dear Mr. Remi," },
  { v: "approved_form", label: "Approved form of address (per contact)" },
  { v: "first_name", label: "First name only — Hello Remi," },
  { v: "neutral", label: "Neutral greeting — Good morning," },
];

export default function P1GreetingPanel({ campaign, recipients, api, onRefresh }) {
  const cid = campaign?.campaign_id;
  const policy = campaign?.greeting_policy || { mode: "title_surname", missing_rule: "hold", neutral_greeting: "Good morning," };
  const [mode, setMode] = useState(policy.mode);
  const [missingRule, setMissingRule] = useState(policy.missing_rule);
  const [neutral, setNeutral] = useState(policy.neutral_greeting || "Good morning,");
  const [holds, setHolds] = useState({ held_count: 0, resumable_count: 0, held: [] });
  const [cleanup, setCleanup] = useState(null);
  const [titles, setTitles] = useState({ defaults: {}, extra: {} });
  const [newTitle, setNewTitle] = useState({ token: "", display: "" });
  const [edit, setEdit] = useState(null); // recipient being corrected
  const [preview, setPreview] = useState(null); // {name, html}
  const [confirmResume, setConfirmResume] = useState(false);
  const [busy, setBusy] = useState(false);

  const contacts = (recipients || []).flatMap((g) => g.contacts || []);

  const loadHolds = useCallback(async () => {
    if (!cid) return;
    try { setHolds(await api(`/campaigns/bespoke/${cid}/holds`)); } catch (e) { /* noop */ }
  }, [cid, api]);
  const loadTitles = useCallback(async () => {
    try { setTitles(await api(`/campaigns/bespoke/config/titles`)); } catch (e) { /* noop */ }
  }, [api]);

  useEffect(() => { loadHolds(); loadTitles(); }, [loadHolds, loadTitles]);

  const savePolicy = async () => {
    setBusy(true);
    try {
      await api(`/campaigns/bespoke/${cid}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ greeting_policy: { mode, missing_rule: missingRule, neutral_greeting: neutral } }),
      });
      toast.success("Greeting policy saved");
      await onRefresh?.(); await loadHolds();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const runCleanupPreview = async () => {
    setBusy(true);
    try { setCleanup(await api(`/campaigns/bespoke/${cid}/cleanup/preview`)); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const applyCleanup = async () => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${cid}/cleanup/apply`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      toast.success(`Cleaned up ${d.applied} recipient(s). Held messages stay held until you resume them.`);
      setCleanup(null); await onRefresh?.(); await loadHolds();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doResume = async () => {
    setBusy(true);
    try {
      const d = await api(`/campaigns/bespoke/${cid}/recipients/resume`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}),
      });
      toast.success(`Resumed ${d.resumed}. ${d.still_held_unresolved} still held (greeting unresolved).`);
      setConfirmResume(false); await onRefresh?.(); await loadHolds();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addTitle = async () => {
    if (!newTitle.token.trim()) return;
    try {
      await api(`/campaigns/bespoke/config/titles`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newTitle),
      });
      setNewTitle({ token: "", display: "" }); await loadTitles();
      toast.success("Title added");
    } catch (e) { toast.error(e.message); }
  };

  const saveCorrection = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await api(`/campaigns/bespoke/${cid}/recipients/${edit.recipient_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honorific: edit.honorific ?? "", first_name: edit.first_name ?? "",
          last_name: edit.last_name ?? "", greeting_override_text: edit.greeting_override_text ?? "",
        }),
      });
      toast.success("Saved. This recipient stays held until you resume them.");
      setEdit(null); await onRefresh?.(); await loadHolds();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="p1-greeting-panel">
      {/* Greeting policy */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ShieldCheck className="w-4 h-4 text-indigo-600" /> Greeting policy
          </div>
          <p className="text-xs text-slate-500">Title-aware by default. Rex never guesses gender or a title from a name. If a required title is missing, recipients are <strong>held for review</strong> unless you choose a neutral fallback.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Greeting style</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="greeting-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GREETING_MODES.map((m) => <SelectItem key={m.v} value={m.v} data-testid={`greeting-mode-${m.v}`}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">If title/name missing</Label>
              <Select value={missingRule} onValueChange={setMissingRule}>
                <SelectTrigger data-testid="missing-rule-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hold" data-testid="missing-rule-hold">Hold for review (default)</SelectItem>
                  <SelectItem value="neutral" data-testid="missing-rule-neutral">Use approved neutral greeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Neutral greeting</Label>
              <Input value={neutral} onChange={(e) => setNeutral(e.target.value)} data-testid="neutral-greeting-input" />
            </div>
          </div>
          <Button onClick={savePolicy} disabled={busy} data-testid="save-greeting-policy-btn">Save policy</Button>
        </CardContent>
      </Card>

      {/* Held queue + explicit resume */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Held queue
              <Badge variant="secondary" data-testid="held-count">{holds.held_count} held</Badge>
              <Badge variant="outline" data-testid="resumable-count">{holds.resumable_count} ready to resume</Badge>
            </div>
            <Dialog open={confirmResume} onOpenChange={setConfirmResume}>
              <DialogTrigger asChild>
                <Button size="sm" disabled={busy || holds.resumable_count === 0} data-testid="resume-open-btn">Resume ready…</Button>
              </DialogTrigger>
              <DialogContent data-testid="resume-confirm-dialog">
                <DialogHeader><DialogTitle>Resume {holds.resumable_count} held recipient(s)?</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-600">Only recipients whose greeting now resolves will be released back into sending. Unresolved ones stay held. Nothing is released automatically — this is your explicit confirmation.</p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmResume(false)}>Cancel</Button>
                  <Button onClick={doResume} disabled={busy} data-testid="resume-confirm-btn">Resume now</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {holds.held.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="no-holds">No recipients are currently held.</p>
          ) : (
            <div className="divide-y rounded border">
              {holds.held.map((h) => (
                <div key={h.recipient_id} className="flex items-center justify-between p-2 text-sm" data-testid={`held-row-${h.recipient_id}`}>
                  <div>
                    <div className="font-medium">{h.name || "(no name)"} <span className="text-slate-400">· {h.company_name}</span></div>
                    <div className="text-xs text-amber-700" data-testid={`hold-reason-${h.recipient_id}`}>{h.hold_reason}</div>
                    {h.resolvable_now && <div className="text-xs text-emerald-700">Now resolvable → {h.proposed_salutation}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => {
                    const c = contacts.find((x) => x.recipient_id === h.recipient_id) || {};
                    setEdit({ recipient_id: h.recipient_id, honorific: c.honorific || "", first_name: c.first_name || "", last_name: c.last_name || "", greeting_override_text: "" });
                  }} data-testid={`correct-btn-${h.recipient_id}`}><PencilLine className="w-4 h-4 mr-1" />Correct</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recipient greeting preview table */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Greeting preview (per recipient)</div>
          <div className="divide-y rounded border max-h-80 overflow-auto">
            {contacts.map((c) => (
              <div key={c.recipient_id} className="flex items-center justify-between p-2 text-sm" data-testid={`greeting-row-${c.recipient_id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name || "(no name)"} <span className="text-slate-400 text-xs">{c.honorific ? `· ${c.honorific}` : ""} {c.email}</span></div>
                  {c.greeting_ready
                    ? <div className="text-xs text-emerald-700" data-testid={`greeting-ok-${c.recipient_id}`}>{c.greeting_salutation}</div>
                    : <div className="text-xs text-amber-700" data-testid={`greeting-held-${c.recipient_id}`}>Held — {c.greeting_hold_reason || "greeting unresolved"}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setPreview({ name: c.name, html: c.bespoke_html || "<p class='text-slate-400'>No draft yet.</p>" })} data-testid={`preview-email-${c.recipient_id}`}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdit({ recipient_id: c.recipient_id, honorific: c.honorific || "", first_name: c.first_name || "", last_name: c.last_name || "", greeting_override_text: "" })} data-testid={`edit-recipient-${c.recipient_id}`}><PencilLine className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-xs text-slate-400 p-2">No recipients yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Cleanup (migration-reviewable) + titles */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles className="w-4 h-4 text-indigo-600" /> Clean up existing names</div>
            <p className="text-xs text-slate-500">Preview normalises UPPERCASE names and extracts titles. User-corrected rows are protected. Applying does <strong>not</strong> send or resume anything.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={runCleanupPreview} disabled={busy} data-testid="cleanup-preview-btn">Preview changes</Button>
              <Button size="sm" onClick={applyCleanup} disabled={busy || !cleanup || cleanup.changed_count === 0} data-testid="cleanup-apply-btn">Apply {cleanup ? `(${cleanup.changed_count})` : ""}</Button>
            </div>
            {cleanup && (
              <div className="text-xs space-y-1 max-h-48 overflow-auto" data-testid="cleanup-preview-result">
                <div className="text-slate-600">{cleanup.changed_count} to change · {cleanup.skipped_count} protected (user-corrected)</div>
                {cleanup.changes.slice(0, 40).map((ch) => (
                  <div key={ch.recipient_id} className="border rounded p-1">
                    <span className="text-slate-400">{ch.original}</span> → <strong>{ch.after.honorific} {ch.after.first_name} {ch.after.last_name}</strong>
                    {ch.greeting_ready ? <span className="text-emerald-700"> · {ch.greeting}</span> : <span className="text-amber-700"> · still held</span>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-sm font-semibold text-slate-800">Recognised titles</div>
            <p className="text-xs text-slate-500">Add a professional, religious or traditional honorific so Rex recognises it (it never assigns one from a name).</p>
            <div className="flex gap-2">
              <Input placeholder="token e.g. oba" value={newTitle.token} onChange={(e) => setNewTitle({ ...newTitle, token: e.target.value })} data-testid="title-token-input" />
              <Input placeholder="display e.g. Oba" value={newTitle.display} onChange={(e) => setNewTitle({ ...newTitle, display: e.target.value })} data-testid="title-display-input" />
              <Button size="sm" onClick={addTitle} data-testid="add-title-btn">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-auto" data-testid="titles-list">
              {Object.values({ ...titles.defaults, ...titles.extra }).filter((v, i, a) => a.indexOf(v) === i).map((d) => (
                <Badge key={d} variant="secondary" className="text-[10px]">{d}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Correction dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="correction-dialog">
          <DialogHeader><DialogTitle>Correct name & greeting</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Title</Label><Input value={edit.honorific} onChange={(e) => setEdit({ ...edit, honorific: e.target.value })} data-testid="edit-honorific" /></div>
                <div><Label className="text-xs">First name</Label><Input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })} data-testid="edit-first-name" /></div>
                <div><Label className="text-xs">Surname</Label><Input value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })} data-testid="edit-last-name" /></div>
              </div>
              <div><Label className="text-xs">Or an exact approved greeting name (overrides the above)</Label>
                <Input placeholder="e.g. Professor Adebayo" value={edit.greeting_override_text} onChange={(e) => setEdit({ ...edit, greeting_override_text: e.target.value })} data-testid="edit-override" /></div>
              <p className="text-xs text-slate-500">Your correction is preserved on re-import and cleanup. The recipient stays held until you explicitly resume.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={saveCorrection} disabled={busy} data-testid="save-correction-btn">Save correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="email-preview-dialog">
          <DialogHeader><DialogTitle>Exact email — {preview?.name}</DialogTitle></DialogHeader>
          <div className="prose prose-sm max-w-none border rounded p-3 bg-white max-h-96 overflow-auto"
               dangerouslySetInnerHTML={{ __html: preview?.html || "" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
